import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Wallet, LayoutDashboard, ArrowLeftRight, CreditCard, TrendingUp,
  RefreshCw, Tags, Wifi,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import { EmptyState } from "@/components/shared/EmptyState";
import NexoCore from "@/components/dashboard/NexoCore";
import NotificationBell from "@/components/shared/NotificationBell";  // FaseC C1
import VisaoTab from "./financeiro/VisaoTab";
import ExtratoTab from "./financeiro/ExtratoTab";
import ContasTab from "./financeiro/ContasTab";
import InvestimentosTab from "./financeiro/InvestimentosTab";
import CategoriasTab from "./financeiro/CategoriasTab";
import AssinaturasTab from "./financeiro/AssinaturasTab";
import ConexoesTab from "./financeiro/ConexoesTab";

// FIN-1 — FinanceSection refatorado pra shell com 7 tabs.
// Default tab Visão (renderiza VisaoTabLegacy enquanto T6-T9 não migram
// pra novo VisaoTab.jsx). Outras 6 tabs viram empty state convidativo.
//
// Sub-routing via hash de 2 níveis (#financeiro/visao|extrato|...).
// Pattern espelhado da Rotina (UI-2). Hash redirect de #finance pra
// #financeiro/visao acontece no NectarDashboard quando integrado.

const TABS = [
  { id: "visao",         label: "Visão",         icon: LayoutDashboard,  tone: "cyan",   component: VisaoTab },
  { id: "extrato",       label: "Extrato",       icon: ArrowLeftRight,   tone: "cyan",   component: ExtratoTab },
  { id: "contas",        label: "Contas",        icon: CreditCard,       tone: "cyan",   component: ContasTab },
  { id: "investimentos", label: "Investimentos", icon: TrendingUp,       tone: "cyan",   component: InvestimentosTab },
  { id: "assinaturas",   label: "Assinaturas",   icon: RefreshCw,        tone: "cyan",   component: AssinaturasTab },
  { id: "categorias",    label: "Categorias",    icon: Tags,             tone: "cyan",   component: CategoriasTab },
  { id: "conexoes",      label: "Conexões",      icon: Wifi,             tone: "cyan",   component: ConexoesTab },
];

function parseHashTab(hash) {
  if (!hash) return null;
  const m = hash.replace(/^#/, "")
    .match(/^financeiro\/(visao|extrato|contas|investimentos|assinaturas|categorias|conexoes)$/);
  return m ? m[1] : null;
}

function FinanceHero({ onMicClick }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.2, 0.8, 0.2, 1] }}
      className="relative overflow-hidden mb-6 md:mb-8 rounded-2xl glass-card-strong gradient-border-cyan glow-hover"
      data-testid="finance-hero"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(circle at 12% 50%, rgba(0,255,163,0.10), transparent 55%)," +
            "radial-gradient(circle at 90% 0%, rgba(0,212,255,0.08), transparent 50%)",
        }}
      />
      <div className="relative px-6 md:px-10 py-8 md:py-10 flex items-center gap-5 md:gap-7">
        <div className="w-14 h-14 md:w-16 md:h-16 rounded-xl bg-brand-green/15 border border-brand-green/30 flex items-center justify-center shadow-glow-green shrink-0">
          <Wallet className="w-7 h-7 md:w-8 md:h-8 text-brand-green" aria-hidden="true" />
        </div>
        <div className="flex-1 min-w-0">
          <h1
            className="font-display font-bold text-foreground text-2xl md:text-[48px] lg:text-[56px] leading-[1.05] tracking-[-0.02em]"
            data-testid="finance-title"
          >
            FINANCEIRO
          </h1>
          <p className="text-body text-foreground-muted mt-1.5 max-w-2xl">
            Suas finanças, organizadas e em tempo real.
          </p>
        </div>
        {/* FaseC C1: sino inline antes do NexoCore mini. */}
        <div className="shrink-0 flex items-center gap-3">
          <NotificationBell />
          {/* NexoCore mini — diferencial vs Trilha. Click delega pro overlay
              de voz via prop (passada de NectarDashboard quando integrar). */}
          <button
            type="button"
            onClick={onMicClick}
            className="hidden md:flex items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-cyan/60"
            aria-label="Falar com Nexo"
            data-testid="finance-nexocore-mini"
          >
            <NexoCore size={64} state="idle" />
          </button>
        </div>
      </div>
    </motion.section>
  );
}

function TabsBar({ activeTab, onSwitch }) {
  const handleKey = (e, idx) => {
    if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
      e.preventDefault();
      const dir = e.key === "ArrowRight" ? 1 : -1;
      const next = TABS[(idx + dir + TABS.length) % TABS.length];
      onSwitch(next.id);
    }
  };

  return (
    <motion.div
      role="tablist"
      aria-label="Tabs do Financeiro"
      /* FaseB B3: scroll horizontal com snap em mobile (era flex-wrap virando
         3 linhas no 390px); flex-wrap normal em desktop. */
      className="flex md:flex-wrap gap-2 mb-6 md:mb-8 overflow-x-auto md:overflow-x-visible scrollbar-hide snap-x snap-mandatory md:snap-none -mx-6 px-6 md:mx-0 md:px-0"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.08, ease: [0.2, 0.8, 0.2, 1] }}
      data-testid="finance-tabs"
    >
      {TABS.map((tab, idx) => {
        const Icon = tab.icon;
        const isActive = activeTab === tab.id;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-controls={`finance-tabpanel-${tab.id}`}
            id={`finance-tab-${tab.id}`}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onSwitch(tab.id)}
            onKeyDown={(e) => handleKey(e, idx)}
            className={cn(
              "inline-flex items-center gap-1.5 md:gap-2 px-3 md:px-5 py-2 md:py-2.5 rounded-lg text-xs md:text-sm font-medium transition-all duration-fast ease-smooth border whitespace-nowrap",
              // FaseB B3: shrink-0 + snap-start pra scroll horizontal funcionar
              // sem comprimir chips em mobile. No-op em desktop (md:flex-wrap).
              "shrink-0 snap-start md:shrink",
              isActive
                ? "bg-brand-cyan/15 border-brand-cyan/40 text-brand-cyan shadow-glow-cyan"
                : "bg-transparent border-subtle text-foreground-muted hover:bg-surface-card-hover hover:text-foreground",
            )}
            data-testid={`finance-tab-${tab.id}`}
          >
            <Icon className="w-4 h-4" aria-hidden="true" />
            {tab.label}
          </button>
        );
      })}
    </motion.div>
  );
}

function ComingSoonState({ tab }) {
  return (
    <Card variant="elevated" className="glass-card py-12 px-8" data-testid={`finance-tabpanel-${tab.id}-empty`}>
      <EmptyState
        icon={tab.icon}
        title="Em desenvolvimento"
        description={`A aba "${tab.label}" será entregue em breve nas próximas sessões. A Visão já tem todas as métricas principais por enquanto.`}
      />
    </Card>
  );
}

export default function FinanceSection({ onMicClick }) {
  const [activeTab, setActiveTab] = useState(() => parseHashTab(window.location.hash) || "visao");

  // Sincroniza hash → tab (back/forward)
  useEffect(() => {
    const onHashChange = () => {
      const t = parseHashTab(window.location.hash);
      if (t && t !== activeTab) setActiveTab(t);
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, [activeTab]);

  // Normaliza hash ao mount (ex: entra direto em #financeiro vira #financeiro/visao)
  useEffect(() => {
    const current = window.location.hash.replace(/^#/, "");
    const expected = `financeiro/${activeTab}`;
    if (current !== expected && (current === "" || current.startsWith("financeiro"))) {
      window.history.replaceState(null, "", `#${expected}`);
    }
  }, [activeTab]);

  const switchTab = useCallback((id) => {
    setActiveTab(id);
    window.history.replaceState(null, "", `#financeiro/${id}`);
  }, []);

  const currentTab = TABS.find(t => t.id === activeTab) || TABS[0];
  const ActiveComponent = currentTab.component;

  return (
    <div className="relative min-h-screen overflow-hidden" data-testid="finance-section">
      {/* UI-1.5 — ambient layers atrás do conteúdo */}
      <div className="absolute inset-0 grid-texture pointer-events-none" aria-hidden="true" />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "var(--ambient-bg-radial)" }}
        aria-hidden="true"
      />
      <div
        className="absolute pointer-events-none"
        style={{
          top: 0, left: 0,
          width: "60%", height: "55%",
          background: "radial-gradient(circle at 18% 25%, rgba(0,255,163,0.08), transparent 50%)",
        }}
        aria-hidden="true"
      />

      <div className="relative px-4 md:px-8 py-6 md:py-8 max-w-[1280px] mx-auto w-full pb-32">
        <FinanceHero onMicClick={onMicClick} />
        <TabsBar activeTab={activeTab} onSwitch={switchTab} />

        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            role="tabpanel"
            id={`finance-tabpanel-${activeTab}`}
            aria-labelledby={`finance-tab-${activeTab}`}
            initial={{ opacity: 0, x: 8 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -8 }}
            transition={{ duration: 0.2, ease: [0.2, 0.8, 0.2, 1] }}
            data-testid={`finance-tabpanel-${activeTab}`}
          >
            {ActiveComponent ? <ActiveComponent onTalk={onMicClick} /> : <ComingSoonState tab={currentTab} />}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
